
import { SubscriptionData } from "../mappers/subscriptionData.ts";
import { StripeExpressCheckoutElementOptions, StripePaymentElementOptions } from "@stripe/stripe-js";
import { ApplePayRecurringPaymentRequestIntervalUnit } from "@stripe/stripe-js/dist/stripe-js/elements/apple-pay";
import { Period } from "../types.ts";
import { PlanBilling } from "../plans.ts";
import { getDateWithDaysOffset } from "../helpers/dateHelper.ts";

// Apple Pay only accepts year/month/day intervals, so quarterly/semestral are
// expressed as a month count rather than their own (invalid) unit.
const APPLE_PAY_INTERVAL: Record<
  Period,
  { unit: ApplePayRecurringPaymentRequestIntervalUnit; count: number }
> = {
  yearly: { unit: "year", count: 1 },
  semestral: { unit: "month", count: 6 },
  quarterly: { unit: "month", count: 3 },
};

const stripeExpressCheckoutTrialOptions = ({
  subscriptionData,
  trialStartDate,
  theme,
  plan,
}: {
  subscriptionData: SubscriptionData,
  trialStartDate: Date,
  theme: 'black' | 'white',
  // Multiplan variant: the selected plan drives the recurring amount + interval.
  // Omitted for control, which falls back to the single subscription plan.
  plan?: PlanBilling,
}): StripeExpressCheckoutElementOptions => {
  const paymentStart = getDateWithDaysOffset( trialStartDate, subscriptionData.getDaysAmount());
  const period = plan?.period ?? subscriptionData.getPaymentData().period;
  // Stripe/Apple Pay needs an integer in minor units; float * 100 can drift
  // (e.g. 69.99 * 100 = 6998.9999…), so round to whole cents.
  const paymentAmount = Math.round(
    (plan?.amount ?? subscriptionData.getPaymentData().amount) * 100
  );
  const interval = APPLE_PAY_INTERVAL[period];

  return {
    buttonTheme: {
      applePay: theme,
      googlePay: theme,
    },
    buttonType: {
      applePay: 'plain',
      googlePay: 'plain',
    },
    paymentMethods: {
      applePay: 'auto',
      googlePay: 'auto',
      link: 'never'
    },
    applePay: {
      recurringPaymentRequest: {
        paymentDescription: "Migiri Subscription",
        managementURL: "https://www.migiri.app/about/en/",
        regularBilling: {
          label: "Migiri Subscription",
          amount: paymentAmount,
          recurringPaymentStartDate: paymentStart,
          recurringPaymentIntervalUnit: interval.unit,
          recurringPaymentIntervalCount: interval.count,
        },
        trialBilling: {
          label: "Trial",
          amount: 0,
          recurringPaymentStartDate: trialStartDate,
          recurringPaymentEndDate: paymentStart,
          recurringPaymentIntervalUnit: "day",
        },
      }
    }
  }
};

const stripeExpressCheckoutPaymentOptions = (theme: 'black' | 'white'): StripeExpressCheckoutElementOptions => {
  return {
    paymentMethodOrder: ['apple_pay', 'google_pay'],
    buttonTheme: {
      applePay: theme,
      googlePay: theme,
    },
    buttonType: {
      applePay: 'plain',
      googlePay: 'plain',
    },
    paymentMethods: {
      applePay: 'auto',
      googlePay: 'auto',
      link: 'never'
    },
  }
}

const stripePaymentOptions: StripePaymentElementOptions = {
  paymentMethodOrder: ['card'],
  wallets: {
    applePay: 'never',
    googlePay: 'never',
  },
}


export { stripeExpressCheckoutTrialOptions, stripeExpressCheckoutPaymentOptions, stripePaymentOptions }